import { legalDocsApi } from 'api/legalDocs'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import useIsThanxAdmin from 'hooks/useIsThanxAdmin'
import useLegalDocsNagDismissal from 'hooks/useLegalDocsNagDismissal'
import { push } from 'react-router-redux'

type UseLegalDocsNagResult = {
  showModal: boolean
  dismiss: () => void
  handleSetupLegalDocs: () => void
}

/**
 * Manages the legal docs nag modal lifecycle.
 * Shows a dismissable modal prompting admins to set up required legal documents
 * when the merchant is not compliant and the feature flag is in 'nag' mode.
 */
export default function useLegalDocsNag(): UseLegalDocsNagResult {
  const dispatch = useDispatch()
  const flag = useFlag('mx-legal-docs-enforcement', 'off')
  const merchant = useCurrentMerchant()
  const isAdmin = useIsAdmin()
  // Thanx admins are not the responsible party for the merchant's legal docs.
  // Treat the user as the merchant's responsible admin only when they admin
  // this merchant AND are not a Thanx admin impersonating, so impersonated
  // sessions don't see merchant-owner reminders.
  const isThanxAdmin = useIsThanxAdmin()
  const isMerchantAdmin = !!isAdmin && !isThanxAdmin
  const shouldCheck = flag === 'nag' && isMerchantAdmin && !!merchant?.id

  const { data, isFetching } = legalDocsApi.useGetComplianceStatusQuery(
    merchant?.id,
    {
      skip: !shouldCheck,
    }
  )

  const [isDismissed, dismiss] = useLegalDocsNagDismissal()

  // Compliance data has loaded (not stale, not re-fetching for a new merchant)
  // and the backend says the merchant is missing required docs.
  const isNonCompliant = !!data && !isFetching && !data.legal_docs_compliant
  // Show the nag modal only when ALL conditions are met:
  // 1. Feature flag is in 'nag' mode (not 'off' or 'block')
  // 2. Current user is the merchant's responsible admin (see isMerchantAdmin)
  // 3. Merchant is non-compliant (see isNonCompliant)
  // 4. User hasn't dismissed the modal this session (1-hour cookie)
  const showModal =
    flag === 'nag' && isMerchantAdmin && isNonCompliant && !isDismissed

  const settingsPageEnabled = useFlag('mx-unavailable-item-visibility', false)

  const handleSetupLegalDocs = () => {
    dismiss()
    dispatch(
      push(
        settingsPageEnabled
          ? '/settings/terms_and_policies'
          : '/brand_content#terms_and_policies'
      )
    )
  }

  return { showModal, dismiss, handleSetupLegalDocs }
}
